import { css } from "@emotion/css";
import { Key, useMemo, useRef, useState } from "react";
import { useFilter } from "react-aria";
import { GhostIconButton } from "@certn/cascadia-react";
import { Text } from "@certn/cascadia/foundations/typography/Text";
import { Collection, Header, ListBoxSection } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import { TagGroup } from "./TagGroup";
import { useRefOffsetWidthResize } from "../../hooks/useRefOffsetWidthResize";
import { _Popover } from "../_assets/_Popover";
import { _Text } from "../_assets/_Text";
import { _Input } from "../inputs/_assets/_Input";
import { _InputWrapper } from "../inputs/_assets/_InputWrapper";
import { FieldErrors } from "../inputs/_assets/input-wrapper/FieldErrors";
import { FormLabelRAC } from "../inputs/_assets/input-wrapper/FormLabelRAC";
import { SelectItem } from "../inputs/select/SelectItem";
import { _ComboBox } from "../inputs/select/assets/_ComboBox";
import { _ListBox } from "../inputs/select/assets/_ListBox";
import { useGetLabelEnum, useGetSectionEnum, useListBoxItems } from "./utils";
import { TaggableValue, TagGroupSelectProps } from "./types";

/**
 * TagGroupSelect requires an array of values objects { id: number, value: string }, as well as a list of selectedValues[number].
 * We use the selectedValues array for both showing which values have been added to the input field and also to display the remaining values inside the popover.
 *** Most instances should use TagGroupSelectFormikField directly
 *** Refer to TagGroupSelect.stories.tsx for more a breakdown
 */
export function TagGroupSelect<T extends object, V extends TaggableValue>(
  props: TagGroupSelectProps<T, V>
) {
  const { t } = useTranslation();
  const {
    groupType = "tag",
    placeholder = t("Search tags"),
    displayHeader = true,
    allowAddingNewValues = true,
    values,
    handleCreateNewValue,
    selectedValues,
    setSelectedValues,
    ...comboBoxProps
  } = props;

  const ref = useRef<HTMLDivElement>(null);
  const { inputWidth } = useRefOffsetWidthResize(ref);
  const { contains } = useFilter({ sensitivity: "base" });
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const SECTIONS = useGetSectionEnum(groupType);
  const LABELS = useGetLabelEnum(groupType);

  const isInputEmpty = !inputValue.trim();
  const hasSelectedValues = !!selectedValues?.length;

  const inputMatchedValue = useMemo(() => {
    return values?.find(
      (value) => value?.tagValue?.toLowerCase() === inputValue?.toLowerCase()
    );
  }, [inputValue, values]);

  const doesInputAlreadyExistAsValue = Boolean(inputMatchedValue);
  const valueAlreadySelected = selectedValues?.find(
    (value) => value?.id === inputMatchedValue?.id
  );

  // omit selected values from popover
  const valuesNotSelected = useMemo(() => {
    if (!values) return [];
    const selectedValueIds = selectedValues.map((value) => value?.id);
    return values.filter((value) => !selectedValueIds.includes(value?.id));
  }, [selectedValues, values]);

  const filteredItems = useMemo(
    () =>
      valuesNotSelected.filter((value) => contains(value.tagValue, inputValue)),
    [valuesNotSelected, inputValue, contains]
  );

  const listBoxItems = useListBoxItems({
    SECTIONS,
    filteredItems,
    inputValue,
    existingItemsLabel: LABELS.EXISTING_ITEMS,
    newItemLabel: LABELS.NEW_ITEM,
  });

  const handleRemove = (key: Set<Key>) => {
    const values = selectedValues.filter(
      (selectedValue) => ![...key].includes(selectedValue?.id)
    );
    setSelectedValues(values);
    setInputValue("");
  };

  const handleAdd = (key: Key | null) => {
    const isNewValue = key === 0;
    const valueID = isNewValue ? uuidv4() : key;
    const value = values?.find((item) => item?.id === key);
    const tagValue = isNewValue
      ? inputValue?.trim()
      : values?.find((item) => item?.id === key)?.tagValue;

    // This construct ensures the new value adheres to the V type
    const newValue = {
      id: valueID as V["id"],
      tagValue: tagValue as V["tagValue"],
      ...(value ?? {}),
    } as V;

    if (isNewValue) {
      if (allowAddingNewValues) {
        handleCreateNewValue?.([...values, newValue]);
        setSelectedValues([...selectedValues, newValue]);
      }
      setInputValue("");
      return;
    }

    // For existing values, always allow selection
    setSelectedValues([...selectedValues, newValue]);
    setInputValue("");
  };

  const renderDynamicTitle = (title: string, id: string) => {
    if (!valuesNotSelected?.length && id === SECTIONS.VALUES) {
      return LABELS.NO_ITEMS_FOUND.toUpperCase();
    }

    if (valueAlreadySelected && id === SECTIONS.NEW_VALUE) {
      return LABELS.ITEM_ALREADY_SELECTED.toUpperCase();
    }

    return title;
  };

  const handleKeyPress = (event: React.KeyboardEvent, input: string) => {
    const existingValue = values?.find((item) => item.tagValue === input);

    // pressing delete inside empty input field should delete last value
    if (event.key === "Backspace" && isInputEmpty) {
      setSelectedValues(selectedValues.slice(0, -1));
    }

    // pressing enter should save the new value if it doesn't already exist
    if (event.key === "Enter" && !isInputEmpty) {
      if (!doesInputAlreadyExistAsValue) {
        handleAdd(0);
      }
      if (existingValue) {
        if (!selectedValues.some((value) => value.id === existingValue.id)) {
          setSelectedValues([...selectedValues, existingValue]);
        }
        setInputValue("");
      }
    }
  };

  return (
    <_ComboBox
      {...comboBoxProps}
      menuTrigger="focus"
      isDisabled={props.isDisabled}
      data-testid={`${props.testID}-combobox`}
      onSelectionChange={handleAdd}
      selectedKey={null} // Set the to null to prevent RAC from controlling selected state
      items={listBoxItems as Iterable<T>}
      aria-label={`${props.name || "Tag Group"} Select ComboBox`}
      onFocusChange={(isFocused) => {
        setIsPopoverOpen(isFocused);
        if (!isFocused) {
          setInputValue("");
        }
      }}
      onOpenChange={(isOpen) => {
        setIsPopoverOpen(isOpen);
      }}
    >
      <FormLabelRAC isRequired={props.isRequired} info={props.info}>
        {props.label}
      </FormLabelRAC>
      <div ref={ref}>
        <_InputWrapper
          datatest-id={`${props.testID}-input-wrapper`}
          className={styles.inputWrapper}
          emptyMessage={props.emptyMessage}
          isEmpty={!values?.length}
        >
          {hasSelectedValues && (
            <TagGroup
              testID={`${props.testID}-selected-tag-group`}
              values={selectedValues}
              handleRemove={handleRemove}
              isDisabled={props.isDisabled}
            />
          )}
          <_Input
            placeholder={selectedValues?.length === 0 ? placeholder : undefined}
            name={props.name}
            value={inputValue}
            onChange={(e) => {
              const value = e?.target?.value;
              // only letters, numbers, underscores and spaces
              setInputValue(value.replace(/[^\w\s]/g, ""));
            }}
            isLoading={props.isLoading}
            data-testid={`${props.testID}-input`}
            className={styles.input}
            onKeyUp={(event) => {
              handleKeyPress(event, event.currentTarget.value);
            }}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => {
              setIsInputFocused(false);
              props.onBlur?.();
            }}
          />
          {!hasSelectedValues && (
            <GhostIconButton
              // RAC shifts popover due to padding in InputWrapper, this Button only
              // exists until Values are selected so this keeps the popover from moving up/down
              className={styles.emptyGhostButton}
              icon={isInputFocused ? "search" : "expand_more"}
            />
          )}
        </_InputWrapper>
      </div>
      {props.description && (
        <_Text style="input/helper" color="text-weak" slot="description">
          {props.description}
        </_Text>
      )}
      <FieldErrors>{props.errors}</FieldErrors>
      <_Popover
        triggerRef={ref}
        isOpen={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        isLoading={props.isLoading}
        className={css({ width: `${inputWidth}px` })}
        testID={`${props.testID}-popover`}
      >
        <_ListBox className={styles.listBox}>
          {(section: Record<string, any>) => {
            const isValuesSection = section?.id === SECTIONS.VALUES;
            const isExistingValueFilterEmpty =
              !filteredItems.length && !isInputEmpty;

            const isNewValueSection = section?.id === SECTIONS.NEW_VALUE;
            const isNewValueMatchingOrEmpty =
              (doesInputAlreadyExistAsValue && !valueAlreadySelected) ||
              isInputEmpty;

            return (
              <ListBoxSection id={section.name}>
                {(isNewValueSection &&
                  (isNewValueMatchingOrEmpty || !allowAddingNewValues)) ||
                (isValuesSection && isExistingValueFilterEmpty) ? undefined : (
                  <>
                    {displayHeader && (
                      <Header
                        className={styles.header}
                        data-testid={`${props.testID}-section-header`}
                      >
                        <Text color="text-weak" style="input/section-title">
                          {renderDynamicTitle(section.name, section.id)}
                        </Text>
                      </Header>
                    )}
                    {/* if value matches existing value hide New Value section or prevent adding new values */}
                    {isNewValueSection &&
                    (doesInputAlreadyExistAsValue ||
                      !allowAddingNewValues) ? undefined : (
                      <Collection items={section.children}>
                        {(value: Record<string, any>) => (
                          <SelectItem
                            className={styles.selectItem}
                            id={value?.id}
                            data-testid={`${value?.id}-select-item`}
                          >
                            {value?.tagValue}
                          </SelectItem>
                        )}
                      </Collection>
                    )}
                  </>
                )}
              </ListBoxSection>
            );
          }}
        </_ListBox>
      </_Popover>
    </_ComboBox>
  );
}

const styles = {
  inputWrapper: css({
    display: "flex",
    flexWrap: "wrap",
    padding: "var(--space-2)",
  }),
  selectItem: css({
    cursor: "pointer",
    height: "var(--space-5)",
    padding: "var(--space-2)",
    margin: "var(--space-1)",
  }),
  header: css({
    padding: "var(--space-2) var(--space-3)",
    display: "flex",
  }),
  listBox: css({ gap: "0px" }),
  input: css({ padding: "0px" }),
  emptyGhostButton: css({ margin: "-8px -8px -8px 0px" }),
};
